import * as _ from 'lodash';
import * as inflection from 'inflection';

export interface Property {
  key: string;
  value: string | number | boolean;
}

export function propertyOr(prop1: any, prop2: any) {
  return (obj: any) => {
    const v1 = _.get(obj, prop1);
    return v1 !== undefined ? v1 : _.get(obj, prop2);
  }
}

export function isPlainObject(value: any): boolean {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

export function isEmpty(obj: Record<string, any>): boolean {
  return Object.keys(obj).length === 0;
}

export function jsonify(o: any) {
  if (o === undefined || o === null) {
    return o;
  }

  return JSON.stringify(o);
}

export function fromJson(s: string | null | undefined) {
  if (!s) {
    return null;
  }

  try {
    return JSON.parse(s);
  } catch (e) {
    return null;
  }
}

const KnownKeys: Record<string, string> = {
  'tnE164': 'tn_e164',
  'sidewinderL3Id': 'sidewinder_l3_id',
}

export function customSnakeCase(key: string): string {
  const knownKey = KnownKeys[key];
  return knownKey || _.snakeCase(key);
}

export function snakerize<T = any>(obj: { [k: string]: any }): T {
  if (!obj) {
    return <T>obj;
  }

  return _.transform<any, any>(obj, (result, val: string, key: string, dict) => {
    result[customSnakeCase(key)] = val;
  });
}

export function snakerizeRecusive<T = any>(obj: { [k: string]: any }): T {
  return !obj ? <T>obj : _.transform<any, any>(obj, (acc, value, key, target) => {
    const camelKey = _.isArray(target) ? key : customSnakeCase(key);
    acc[camelKey] = _.isObject(value) ? snakerizeRecusive(value) : value;
  });
}

const KnownCamelizeKeys: Record<string, string> = {
  'tn_e164s': 'tnE164s',
}

export function customCamelizeCase(key: string): string {
  const knownKey = KnownCamelizeKeys[key];
  return knownKey || _.camelCase(key);
}
export function camelize<T = any>(obj: { [k: string]: any }): T {
  if (!obj) {
    return <T>obj;
  }
  return _.transform<any, any>(obj, (result, val: string, key: string, dict) => {
    result[customCamelizeCase(key)] = val;
  });
}

export function camelizeRecusive<T = any>(obj: { [k: string]: any }): T {
  return !obj ? <T>obj : _.transform<any, any>(obj, (acc, value, key, target) => {
    const camelKey = _.isArray(target) ? key : customCamelizeCase(key);
    acc[camelKey] = _.isObject(value) ? camelizeRecusive(value) : value;
  });
}

export function classifize<T = any>(obj: { [k: string]: any }): T {
  if (!obj) {
    return <T>obj;
  }

  return _.transform<any, any>(obj, (result, val: string, key: string, dict) => {
    result[inflection.classify(key)] = val;
  });
}

export function keyValuesToObject(configuration: any) {
  return _.chain(configuration)
    .keyBy('key')
    .mapValues('value')
    .value();
}

export function objectToKeyValues(configuration: any): Property[] {
  return _.map(configuration || {}, (value: string | number | boolean, key: string) => ({ key, value }))
}

export function mergeDefaults<T>(defaults: T, overrides: Partial<T>): T {
  return {
    ...defaults,
    ...Object.fromEntries(
      Object.entries(overrides).filter(([_unused, value]) => value != null)
    ),
  };
}

export function mergeDefaultsDeep<T>(defaults: T, overrides: Partial<T>): T {
  return {
    ...defaults,
    ...Object.fromEntries(
      Object.entries(overrides).filter(([_key, value]) => value != null)
    ),
    ...Object.fromEntries(
      Object.entries(defaults as Record<string, unknown>).map(([key, defaultValue]) => {
        const overrideValue = (overrides as any)[key];
        if (
          typeof defaultValue === 'object' &&
          defaultValue !== null &&
          !Array.isArray(defaultValue) &&
          typeof overrideValue === 'object' &&
          overrideValue !== null &&
          !Array.isArray(overrideValue)
        ) {
          return [key, mergeDefaults(defaultValue, overrideValue)];
        }
        return [key, overrideValue ?? defaultValue];
      })
    ),
  };
}


export function incrementKey(memo: Record<string, number>, key: string, qty = 1) {
  memo[key] = (memo[key] || 0) + qty;
  return memo;
}

export function omit(obj: any, args: any) {
  return _.omit(obj, args);
}

export function omitBy(obj: any, callback: any) {
  return _.omitBy(obj, callback);
}

export function omitNulls(obj: any) {
  return _.omitBy(obj, _.isNil);
}

export function capitalizeKeys(jsObject: any): any {
  if (!jsObject) {
    return jsObject;
  }
  return _.transform<any, any>(
    jsObject,
    (result, val: string | any, key: string, dict) => {
      if (_.isArray(val)) {
        return (result[key.charAt(0).toUpperCase() + key.slice(1)] = val.map(
          (v) => (_.isObject(v) ? capitalizeKeys(v) : v)
        ));
      }

      return _.isObject(val)
        ? (result[key.charAt(0).toUpperCase() + key.slice(1)] =
          capitalizeKeys(val))
        : (result[key.charAt(0).toUpperCase() + key.slice(1)] = val);
    }
  );
}

export function objectDifferences(original: any, newObject: any) {
  return _.transform(newObject, (result: any, value, key) => {
    if (value === null && original[key] === undefined) {
      return;
    }

    if (!_.isEqual(value, original[key])) {
      result[key] = _.isObject(value) && _.isObject(original[key])
        ? objectDifferences(original[key], value)
        : value;
    }
  });
}

function pascalCase(str: string) {
  return _.startCase(str).replace(/ /g, '');
}

export function pascalize<T = any>(obj: { [k: string]: any }): T {
  if (!obj) {
    return <T>obj;
  }
  return _.transform<any, any>(obj, (result, val: string, key: string) => {
    result[pascalCase(key)] = val;
  });
}

export function pascalizeRecursive<T = any>(obj: { [k: string]: any }): T {
  return !obj ? <T>obj : _.transform<any, any>(obj, (acc, value, key, target) => {
    const pascalKey = _.isArray(target) ? key : pascalCase(key);
    acc[pascalKey] = _.isObject(value) ? pascalizeRecursive(value) : value;
  });
}

export function renameAttribute(obj: any, currentName: string, newName: string) {
  if (!_.isPlainObject(obj)) {
    throw new Error('First parameter must be an object');
  }

  if (!_.isString(currentName) || !_.isString(newName)) {
    throw new Error('Second and third parameters must be strings');
  }

  const newObj = _.cloneDeep(obj);

  function rename(obj: any) {
    _.forEach(obj, (value, key) => {
      if (key === currentName) {
        _.set(obj, newName, value);
        _.unset(obj, currentName);
      } else if (_.isPlainObject(value)) {
        rename(value);
      }
    });
  }

  rename(newObj);
  return newObj;
}

export function updateAttribute(obj: any, attribute: string, value: string | number | boolean | object) {
  if (!_.isPlainObject(obj)) {
    throw new Error('First parameter must be an object');
  }

  const newObj = _.cloneDeep(obj);

  function updateInObject(objToUpdate: any) {
    for (const key in objToUpdate) {
      if (_.isPlainObject(objToUpdate[key])) {
        // Update attribute in nested objects
        _.set(objToUpdate[key], attribute, value);
        // Recursively update nested objects
        updateInObject(objToUpdate[key]);
      } else if (Array.isArray(objToUpdate[key])) {
        // Recursively update nested arrays
        updateInArray(objToUpdate[key]);
      }
    }
  }

  function updateInArray(arr: any[]) {
    arr.forEach((item, index) => {
      if (_.isPlainObject(item)) {
        // Update attribute in the object
        _.set(arr[index], attribute, value);
        // Recursively update nested objects
        updateInObject(item);
      } else if (Array.isArray(item)) {
        // Recursively update nested arrays
        updateInArray(item);
      }
    });
  }

  // Update attribute in the top-level object
  _.set(newObj, attribute, value);
  // Recursively update nested objects and arrays
  updateInObject(newObj);

  return newObj;
}

export function convertAttributeToArray(obj: any, attribute: string) {
  if (!_.isPlainObject(obj)) {
    throw new Error('First parameter must be an object');
  }

  const newObj = _.cloneDeep(obj);

  function convertAttribute(obj: any, attr: string) {
    if (_.has(obj, attr)) {
      const value = _.get(obj, attr);
      if (!_.isArray(value)) {
        _.set(obj, attr, [value]);
      }
    }

    for (const key in obj) {
      if (_.isPlainObject(obj[key])) {
        convertAttribute(obj[key], attr);
      }
    }
  }

  convertAttribute(newObj, attribute);

  return newObj;
}

export function convertAttributeToNotArray(obj: any, attribute: string) {
  if (!_.isPlainObject(obj)) {
    throw new Error('First parameter must be an object');
  }

  const newObj = _.cloneDeep(obj);

  function convertAttribute(obj: any, attr: string) {
    if (_.has(obj, attr)) {
      const value = _.get(obj, attr);
      if (_.isArray(value) && value.length === 1) {
        _.set(obj, attr, value[0]);
      }
    }

    for (const key in obj) {
      if (_.isPlainObject(obj[key])) {
        convertAttribute(obj[key], attr);
      }
    }
  }

  convertAttribute(newObj, attribute);

  return newObj;
}

export function removeTypename(obj: any) {
  if (!obj || typeof obj !== 'object') {
    return obj;
  }

  const newObj: any = omit(obj, '__typename');

  for (const key in newObj) {
    if (typeof newObj[key] === 'object') {
      newObj[key] = removeTypename(newObj[key]);
    }
  }

  return newObj;
}


export function renameFields(obj: any, fieldMap: { [key: string]: string }) {
  if (!obj || typeof obj !== 'object') {
    return obj;
  }

  const newObj = _.cloneDeep(obj);

  function rename(obj: any) {
    _.forEach(obj, (value, key) => {
      if (fieldMap[key]) {
        obj[fieldMap[key]] = value;
        delete obj[key];
      } else if (_.isPlainObject(value)) {
        rename(value);
      }
    });
  }

  rename(newObj);
  return newObj;
}

export function notNullFieldsOfObject(obj: any) {
  if (!obj) return null;
  return Object.fromEntries(Object.entries(obj).filter(([_, v]) => v !== null && v !== undefined));
}

export function NotNullKeysOfObject(obj: any) {
  if (!obj) return null;
  return Object.keys(obj).filter(k => obj[k] !== null && obj[k] !== undefined);
}

const KnownKebabKeys: Record<string, string> = {
  //
};

export function customKebabCase(key: string): string {
  const knownKey = KnownKebabKeys[key];
  return knownKey || _.kebabCase(key);
}

export function kebabize<T = any>(obj: { [k: string]: any }): T {
  if (!obj) {
    return obj as T;
  }

  return _.transform<any, any>(obj, (result, val, key) => {
    result[customKebabCase(key)] = val;
  });
}

export function kebabizeRecursive<T = any>(obj: { [k: string]: any }): T {
  if (!obj) return obj as T;

  return _.transform<any, any>(obj, (acc, value, key, target) => {
    const kebabKey = _.isArray(target) ? key : customKebabCase(key);
    acc[kebabKey] = _.isObject(value) ? kebabizeRecursive(value) : value;
  });
}


// Type-level case detection and conversion
type IsUpperCase<S extends string> = S extends `${infer First}${string}`
  ? First extends Uppercase<First>
  ? true
  : false
  : false;

type ToUpperCamelCase<S extends string> = S extends `${infer First}${infer Rest}`
  ? `${Uppercase<First>}${Rest}`
  : S;

type ToLowerCamelCase<S extends string> = S extends `${infer First}${infer Rest}`
  ? `${Lowercase<First>}${Rest}`
  : S;

type FlipCase<S extends string> = IsUpperCase<S> extends true
  ? ToLowerCamelCase<S>
  : ToUpperCamelCase<S>;

export type FlipKeysCase<T> = {
  [K in keyof T as FlipCase<K & string>]: T[K] extends object
  ? T[K] extends any[]
  ? T[K] extends (infer U)[]
  ? U extends object
  ? FlipKeysCase<U>[]
  : U[]
  : T[K]
  : FlipKeysCase<T[K]>
  : T[K];
};

// Runtime conversion
function isUpperCase(char: string): boolean {
  return char === char.toUpperCase() && char !== char.toLowerCase();
}

function flipCase(str: string): string {
  if (str.length === 0) return str;
  const firstChar = str.charAt(0);

  if (isUpperCase(firstChar)) {
    return firstChar.toLowerCase() + str.slice(1);
  } else {
    return firstChar.toUpperCase() + str.slice(1);
  }
}

export function flipKeysCase<T>(obj: T): FlipKeysCase<T> {
  if (obj === null || typeof obj !== 'object') {
    return obj as any;
  }

  if (Array.isArray(obj)) {
    return obj.map(item => flipKeysCase(item)) as any;
  }

  return Object.keys(obj).reduce((acc, key) => {
    const flippedKey = flipCase(key);
    acc[flippedKey] = flipKeysCase((obj as any)[key]);
    return acc;
  }, {} as any);
}
